import { useState, useRef, useCallback, useMemo } from 'react'
import { Plus } from 'lucide-react'
import type { CanvasSection } from './canvasTemplates'
import type { CanvasSectionData } from '@/types/canvas'
import { useUpdateEntityLink } from '@/hooks/useCanvasInstances'
import { useMarketSegmentation } from '@/hooks/useMarketSegmentation'
import { formatCurrency } from '@/components/market-segmentation/sizing/sizingView'
import type { MarketSegmentWithScore } from '@ispot/shared'

const BMC_GROUPS = [
  {
    domain: 'market' as const,
    summary: 'who has the problem.',
    sectionIds: ['customer-segments', 'customer-relationships', 'channels'],
  },
  {
    domain: 'solution' as const,
    summary: 'why they buy.',
    sectionIds: ['value-propositions'],
  },
  {
    domain: 'business' as const,
    summary: 'how it works.',
    sectionIds: ['key-partners', 'key-activities', 'key-resources', 'cost-structure', 'revenue-streams'],
  },
]

const DOMAIN_COLOR: Record<string, string> = {
  market: 'var(--color-market, #2BBCC7)',
  business: 'var(--color-business, #3196E2)',
  solution: 'var(--color-solution, #FFC05F)',
}

const DOMAIN_INK: Record<string, string> = {
  market: '#00727a',
  business: '#0a5d92',
  solution: '#8a5a10',
}

const TIER: Record<number, { label: string; color: string; bg: string }> = {
  0: { label: 'Empty', color: '#9a9490', bg: 'var(--color-bg-secondary, #f4f2ed)' },
  1: { label: 'Developing', color: '#9a6309', bg: '#fbf2e2' },
  2: { label: 'In review', color: '#5a6b75', bg: 'var(--color-bg-secondary, #f4f2ed)' },
  3: { label: 'Validated', color: '#366d44', bg: '#eef6f0' },
}

function getTier(count: number) {
  if (count === 0) return TIER[0]
  if (count < 2) return TIER[1]
  if (count < 4) return TIER[2]
  return TIER[3]
}

const BRIEF_TABS = ['Description', 'Evidence', 'Connections', 'Skerpa']

interface Props {
  canvasUri: string | null
  sections: CanvasSection[]
  sectionData: CanvasSectionData[]
  accountId: string
  projectId: string
  onUnlinkItem: (sectionId: string, itemUri: string) => void
  onRequestCreate: (section: CanvasSection) => void
  onSelectItem: (uri: string, sectionId: string) => void
}

export function CanvasBMCOutlineView({
  canvasUri,
  sections,
  sectionData,
  accountId,
  projectId,
  onRequestCreate,
  onSelectItem,
}: Props) {
  const updateEntityLink = useUpdateEntityLink()
  const { segments } = useMarketSegmentation(accountId, projectId)
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const [tabs, setTabs] = useState<Record<string, string>>({})
  const [editingNote, setEditingNote] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const labelRefs = useRef<Record<string, HTMLSpanElement | null>>({})

  const tab = (uri: string) => tabs[uri] ?? 'Description'

  // Build a lookup from graphEntityUri → segment for customer-segments enrichment
  const segmentByEntityUri = useMemo(() => {
    const map = new Map<string, typeof segments[number]>()
    for (const seg of segments) {
      if (seg.graphEntityUri) map.set(seg.graphEntityUri, seg)
    }
    return map
  }, [segments])

  const commitLabel = useCallback((
    uri: string,
    linkId: string | undefined,
    originalLabel: string
  ) => {
    const el = labelRefs.current[uri]
    if (!el || !linkId || !canvasUri) return
    const newLabel = el.textContent?.trim() ?? ''
    if (!newLabel || newLabel === originalLabel) {
      el.textContent = originalLabel
      return
    }
    updateEntityLink.mutate({ canvasUri, linkId, entityLabel: newLabel })
  }, [canvasUri, updateEntityLink])

  const commitNote = useCallback((
    uri: string,
    linkId: string | undefined
  ) => {
    if (!linkId || !canvasUri) return
    updateEntityLink.mutate({ canvasUri, linkId, contextNote: noteText || null })
    setEditingNote(null)
  }, [canvasUri, noteText, updateEntityLink])

  return (
    <div style={{ padding: '32px 56px 80px 72px', maxWidth: 1160, position: 'relative' }}>
      {BMC_GROUPS.map(({ domain, summary, sectionIds }, gi) => {
        const groupSections = sectionIds
          .map(id => sections.find(s => s.id === id))
          .filter((s): s is CanvasSection => s !== undefined)
        if (groupSections.length === 0) return null

        const color = DOMAIN_COLOR[domain]
        const ink = DOMAIN_INK[domain]
        const domainLabel = domain.charAt(0).toUpperCase() + domain.slice(1)

        return (
          <div key={domain} style={{ marginTop: gi === 0 ? 0 : 56 }}>
            {/* Domain heading */}
            <div style={{ position: 'relative', padding: '8px 0 18px' }}>
              <div style={{ position: 'absolute', left: -32, top: 14, bottom: 12, width: 2, borderRadius: 2, background: color }} />
              <h2 style={{ margin: 0, fontWeight: 400, fontSize: 22, letterSpacing: '-0.012em', lineHeight: 1.3, fontStyle: 'italic', color: 'var(--color-fg, #1e3643)' }}>
                <span style={{ color: ink }}>{domainLabel}</span>
                <span style={{ color: 'rgba(154,148,144,0.5)', margin: '0 8px', fontStyle: 'normal' }}>—</span>
                <span style={{ color: 'var(--color-fg-muted, #9a9490)' }}>{summary}</span>
              </h2>
            </div>

            {groupSections.map((section) => {
              const sd = sectionData.find(d => d.sectionId === section.id)
              const items = sd?.linkedItems ?? []
              const tier = getTier(items.length)
              const isCustomerSegments = section.id === 'customer-segments'

              return (
                <div
                  key={section.id}
                  style={{
                    padding: '32px 0',
                    borderTop: '1px solid var(--color-border, #eceae5)',
                    display: 'grid',
                    gridTemplateColumns: '240px 1fr',
                    columnGap: 48,
                    alignItems: 'start',
                  }}
                >
                  {/* Sticky meta column */}
                  <div style={{ position: 'sticky', top: 32 }}>
                    <h3 style={{ margin: 0, fontWeight: 450, fontSize: 18, letterSpacing: '-0.012em', lineHeight: 1.3, color: 'var(--color-fg, #1e3643)' }}>
                      {section.title}
                    </h3>
                    <p style={{ margin: '6px 0 0', color: 'var(--color-fg-muted, #9a9490)', fontSize: 13, lineHeight: 1.55, fontStyle: 'italic' }}>
                      {section.description}
                    </p>
                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--color-fg-secondary, #5a6b75)', fontVariantNumeric: 'tabular-nums' }}>
                        {items.length} {items.length === 1 ? 'item' : 'items'}
                      </span>
                      <span style={{ fontSize: 12, padding: '1px 8px', borderRadius: 999, lineHeight: 1.5, color: tier.color, background: tier.bg }}>
                        {tier.label}
                      </span>
                    </div>
                  </div>

                  {/* Items */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {items.length === 0 && (
                      <p style={{ padding: '4px 0 16px', color: 'var(--color-fg-muted, #9a9490)', fontSize: 13.5, lineHeight: 1.65 }}>
                        No items yet.
                      </p>
                    )}

                    {items.map((item, idx) => {
                      const isExpanded = expandedItem === item.instanceUri
                      const activeTab = tab(item.instanceUri)
                      const isEditingNote = editingNote === item.instanceUri
                      const segment = isCustomerSegments ? segmentByEntityUri.get(item.instanceUri) : undefined

                      return (
                        <div
                          key={item.instanceUri}
                          style={{
                            paddingTop: idx === 0 ? 4 : 18,
                            paddingBottom: isExpanded ? 20 : 18,
                            paddingLeft: isExpanded ? 20 : 0,
                            paddingRight: isExpanded ? 20 : 0,
                            borderTop: idx === 0 ? 'none' : '1px solid var(--color-border, #eceae5)',
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            columnGap: 24,
                            alignItems: 'start',
                            background: isExpanded ? 'var(--color-bg-secondary, #f4f2ed)' : 'transparent',
                            borderRadius: isExpanded ? 6 : 0,
                            margin: isExpanded ? '12px -20px' : 0,
                          }}
                        >
                          {/* Item content */}
                          <div>
                            {/* Editable label — block so note sits below */}
                            <span
                              ref={el => { labelRefs.current[item.instanceUri] = el }}
                              contentEditable
                              suppressContentEditableWarning
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
                                if (e.key === 'Escape') { e.currentTarget.textContent = item.label; e.currentTarget.blur() }
                              }}
                              onBlur={() => { void commitLabel(item.instanceUri, item.linkId, item.label) }}
                              style={{
                                fontSize: 15.5, fontWeight: 450, letterSpacing: '-0.008em',
                                color: 'var(--color-fg, #1e3643)', lineHeight: 1.4,
                                borderBottom: '1px dotted transparent',
                                paddingBottom: 1, cursor: 'text', outline: 'none',
                                display: 'block',
                              }}
                            >
                              {item.label}
                            </span>

                            {/* Segment sizing chips — TAM / SAM / SOM */}
                            {segment && (
                              <SegmentSizingChips segment={segment} />
                            )}

                            {/* Note / description — editable inline */}
                            {isEditingNote ? (
                              <textarea
                                autoFocus
                                value={noteText}
                                onChange={e => setNoteText(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Escape') { setEditingNote(null) }
                                  if (e.key === 'Enter' && e.metaKey) { void commitNote(item.instanceUri, item.linkId) }
                                }}
                                onBlur={() => void commitNote(item.instanceUri, item.linkId)}
                                placeholder="Add a canvas note..."
                                rows={2}
                                style={{
                                  display: 'block', width: '100%', marginTop: 6,
                                  padding: '6px 8px', border: '1px solid var(--color-border, #eceae5)',
                                  borderRadius: 4, fontFamily: 'inherit', fontSize: 13.5,
                                  lineHeight: 1.65, color: 'var(--color-fg, #1e3643)',
                                  background: 'var(--color-bg-card, white)', resize: 'vertical',
                                  outline: 'none', maxWidth: '64ch',
                                }}
                              />
                            ) : item.note ? (
                              <p
                                onClick={() => { setEditingNote(item.instanceUri); setNoteText(item.note ?? '') }}
                                style={{
                                  margin: '6px 0 0', color: 'var(--color-fg-secondary, #5a6b75)',
                                  fontSize: 13.5, lineHeight: 1.65, maxWidth: '64ch', cursor: 'text',
                                  borderBottom: '1px dotted transparent',
                                }}
                              >
                                {item.note}
                              </p>
                            ) : segment?.description ? (
                              /* Segment description from market segmentation — read-only source */
                              <p
                                style={{
                                  margin: '6px 0 0', color: 'var(--color-fg-secondary, #5a6b75)',
                                  fontSize: 13.5, lineHeight: 1.65, maxWidth: '64ch',
                                }}
                              >
                                {segment.description}
                              </p>
                            ) : (
                              <button
                                onClick={() => { setEditingNote(item.instanceUri); setNoteText('') }}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 6,
                                  marginTop: 4, background: 'none', border: 'none',
                                  color: 'var(--color-fg-muted, #9a9490)', fontSize: 13,
                                  fontStyle: 'italic', cursor: 'pointer', fontFamily: 'inherit',
                                  padding: 0,
                                }}
                              >
                                Add a description
                              </button>
                            )}

                            {/* Expanded brief */}
                            {isExpanded && (
                              <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--color-border, #eceae5)' }}>
                                <div style={{ display: 'flex', gap: 0, marginBottom: 18, borderBottom: '1px solid var(--color-border, #eceae5)' }}>
                                  {BRIEF_TABS.map(t => (
                                    <button
                                      key={t}
                                      onClick={() => setTabs(prev => ({ ...prev, [item.instanceUri]: t }))}
                                      style={{
                                        background: 'transparent', border: 'none', fontFamily: 'inherit',
                                        fontSize: 13, cursor: 'pointer', padding: '6px 12px 8px',
                                        color: activeTab === t ? 'var(--color-fg, #1e3643)' : 'var(--color-fg-muted, #9a9490)',
                                        borderBottom: `1px solid ${activeTab === t ? 'var(--color-fg, #1e3643)' : 'transparent'}`,
                                        marginBottom: -1, transition: 'color 0.15s',
                                      }}
                                    >
                                      {t}
                                    </button>
                                  ))}
                                </div>
                                {activeTab === 'Description' && (
                                  item.note
                                    ? <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--color-fg, #1e3643)', maxWidth: '64ch' }}>{item.note}</p>
                                    : <p style={{ margin: 0, fontSize: 13.5, color: 'var(--color-fg-muted, #9a9490)', fontStyle: 'italic' }}>No description yet. Click above to add one.</p>
                                )}
                                {activeTab !== 'Description' && (
                                  <div>
                                    <p style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--color-fg-muted, #9a9490)', fontStyle: 'italic' }}>
                                      {activeTab === 'Skerpa' ? 'Ask Skerpa about this item.' : `No ${activeTab.toLowerCase()} linked yet.`}
                                    </p>
                                    <button
                                      onClick={() => onSelectItem(item.instanceUri, section.id)}
                                      style={{
                                        background: 'transparent', border: '1px solid var(--color-border, #eceae5)',
                                        color: 'var(--color-fg-secondary, #5a6b75)', fontFamily: 'inherit', fontSize: 12.5,
                                        padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                      }}
                                    >
                                      Open full detail →
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Right column */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, paddingTop: 4 }}>
                            <div style={{ width: 6, height: 6, borderRadius: 999, background: '#449a59', flexShrink: 0 }} />
                            <button
                              onClick={() => {
                                if (isExpanded) {
                                  setExpandedItem(null)
                                } else {
                                  setExpandedItem(item.instanceUri)
                                  onSelectItem(item.instanceUri, section.id)
                                }
                              }}
                              style={{
                                background: 'transparent', border: 'none', fontFamily: 'inherit',
                                color: 'var(--color-fg-muted, #9a9490)', fontSize: 12, padding: '2px 4px',
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {isExpanded ? 'Close' : 'Open detail'} →
                            </button>
                          </div>
                        </div>
                      )
                    })}

                    {/* Add row */}
                    <div style={{ display: 'flex', gap: 10, marginTop: items.length > 0 ? 18 : 4 }}>
                      <button
                        onClick={() => onRequestCreate(section)}
                        style={{
                          flex: 1, border: '1px dashed var(--color-border-strong, #dedbd4)',
                          background: 'transparent', padding: '10px 14px',
                          color: 'var(--color-fg-muted, #9a9490)', fontFamily: 'inherit', fontSize: 13,
                          borderRadius: 6, cursor: 'pointer', textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 10,
                        }}
                      >
                        <Plus size={13} />
                        Add {section.title.toLowerCase()}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Segment sizing chips
// ---------------------------------------------------------------------------

interface SegmentSizingChipsProps {
  segment: MarketSegmentWithScore
}

function SegmentSizingChips({ segment }: SegmentSizingChipsProps) {
  const { tam, samTopDown, samBottomUp, som } = segment.sizing
  // Use samTopDown if available, else samBottomUp
  const sam = samTopDown.value != null ? samTopDown : samBottomUp

  const chips = [
    { label: 'TAM', value: tam.value },
    { label: 'SAM', value: sam.value },
    { label: 'SOM', value: som.value },
  ].filter(c => c.value != null)

  const score = Math.round(segment.weightedScore)

  if (chips.length === 0 && score === 0) return null

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {chips.map(({ label, value }) => (
        <span
          key={label}
          style={{
            fontSize: 11.5, padding: '1px 7px', borderRadius: 4,
            background: 'var(--color-bg-secondary, #f4f2ed)',
            color: 'var(--color-fg-secondary, #5a6b75)',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.6,
            letterSpacing: '0.01em',
          }}
        >
          <span style={{ color: 'var(--color-fg-muted, #9a9490)', marginRight: 3 }}>{label}</span>
          {formatCurrency(value)}
        </span>
      ))}
      {score > 0 && (
        <span
          style={{
            fontSize: 11.5, padding: '1px 7px', borderRadius: 4,
            background: '#eef6f0', color: '#366d44',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.6,
          }}
        >
          Score {score}/100
        </span>
      )}
    </div>
  )
}
